// Renders a manifest as a fullscreen looping playlist with persistent overlay.
// Used by both the live player route and the admin preview.
//
// The component is "dumb" about caching — callers pass a resolver that turns
// a manifest URL into a usable URL (blob URL when cached, signed URL when not).

import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  Manifest,
  ManifestAudioPlaylistItem,
  ManifestImageVariant,
  ManifestOverlay,
  ManifestPlaylistItem,
  ManifestTextVariant,
  OverlayVariantKey,
} from '@/types/manifest';
import { WeatherWidget } from './WeatherWidget';
import { prepareVectorSignalWallboardWebpageUrl } from './vectorSignalWallboard';
import type { PlayEventsBuffer, PlayItemKind } from '@/lib/play-events';

interface ResolveHint {
  kind: 'asset' | 'overlay';
  /** Asset id for playlist items, or overlay variant key for overlays. */
  key: string;
  /** Optional SHA-256 content hash from the manifest, used for content-addressed lookups. */
  contentHash?: string | null;
}

interface Props {
  manifest: Manifest;
  /**
   * Resolve a manifest URL into a usable display URL. The first argument may
   * be empty when the manifest has no baked URL (v2 manifests); the resolver
   * then needs to look the URL up via cache or runtime bundle.
   */
  resolveUrl: (url: string, hint: ResolveHint) => Promise<string> | string;
  /**
   * Optional Proof-of-Play event emitter. Live player passes the engine's
   * per-screen buffer; admin preview omits it so previews do not pollute
   * production telemetry.
   */
  playEvents?: PlayEventsBuffer | null;
  /**
   * Revision id tagged onto emitted play events. Required when
   * `playEvents` is supplied; ignored otherwise.
   */
  revisionId?: string | null;
}

interface NativeTvWebpageBridge {
  showWebpage?: (url: string) => void;
  hideWebpage?: () => void;
}

declare global {
  interface Window {
    SignageFireTV?: NativeTvWebpageBridge;
    SignageGoogleTV?: NativeTvWebpageBridge;
  }
}

function getNativeTvWebpageBridge(): NativeTvWebpageBridge | null {
  if (typeof window === 'undefined') return null;
  const bridge = window.SignageFireTV ?? window.SignageGoogleTV ?? null;
  if (!bridge?.showWebpage || !bridge?.hideWebpage) return null;
  return bridge;
}

// Tolerance for declaring an item "completed". Image/weather/webpage items
// advance via setTimeout, which can drift a few ms; videos may dispatch
// `ended` slightly before scheduledDurationMs depending on encoded length.
const PLAY_COMPLETION_TOLERANCE_MS = 250;

export function PlayerStage({ manifest, resolveUrl, playEvents, revisionId }: Props) {
  const [index, setIndex] = useState(0);
  const playlist = manifest.playlist;
  const item = playlist[index] ?? null;

  // Wrap onEnd into a stable callback for the active item.
  const advance = () => setIndex((i) => (playlist.length === 0 ? 0 : (i + 1) % playlist.length));

  // Reset to start when the manifest changes.
  useEffect(() => setIndex(0), [manifest.revision, manifest.screenId]);

  if (!item) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-black text-sm text-white/60">
        Empty playlist
      </div>
    );
  }

  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ background: manifest.canvas.background ?? '#000' }}
    >
      <PlayItemTracker
        key={`${revisionId ?? 'no-rev'}:${item.id}`}
        emitter={playEvents ?? null}
        revisionId={revisionId ?? null}
        item={item}
        index={index}
      >
        {item.kind === 'webpage' ? (
          <WebpageItem item={item} onEnd={advance} />
        ) : item.kind === 'weather' ? (
          <WeatherItem item={item} onEnd={advance} />
        ) : (
          <PlaylistItem item={item} resolveUrl={resolveUrl} onEnd={advance} />
        )}
      </PlayItemTracker>
      <OverlayLayer overlay={manifest.overlay} item={item} resolveUrl={resolveUrl} />
      <BackgroundAudio tracks={manifest.audioPlaylist ?? []} resolveUrl={resolveUrl} />
    </div>
  );
}

/**
 * Records Proof-of-Play start/end events for the currently rendered playlist
 * item. Keyed by `revisionId:item.id` upstream, so this component mounts
 * when an item begins and unmounts when it advances (timer fires, video
 * ends, or the manifest swaps mid-item). The revision dimension matters
 * because publishes commonly preserve draft item IDs across revisions — a
 * pure `item.id` key would leave the tracker mounted across a revision
 * swap, leaking the previous revision's event into the new revision and
 * skipping a fresh start. The unmount path computes completion status
 * from actual vs scheduled duration so a smooth advance counts as
 * 'completed' and a mid-item interruption counts as 'partial'.
 */
function PlayItemTracker({
  emitter,
  item,
  index,
  revisionId,
  children,
}: {
  emitter: PlayEventsBuffer | null;
  item: ManifestPlaylistItem;
  index: number;
  revisionId: string | null;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!emitter) return;
    const startInput = {
      revisionId,
      assetId: item.assetId || null,
      itemKind: item.kind as PlayItemKind,
      manifestItemIndex: index,
      manifestItemId: item.id,
      scheduledDurationMs: Math.max(1, item.durationSeconds) * 1000,
    };
    const handle = emitter.recordStart(startInput);
    return () => {
      const actualDurationMs = Date.now() - handle.startedAt;
      const completed =
        actualDurationMs + PLAY_COMPLETION_TOLERANCE_MS >= startInput.scheduledDurationMs;
      void emitter.recordEnd(handle, startInput, {
        actualDurationMs,
        completionStatus: completed ? 'completed' : 'partial',
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, revisionId]);
  return <>{children}</>;
}

/**
 * Plays the audio playlist as an independent loop alongside the visual stage.
 * Tracks always play to completion; the loop wraps once the last track ends.
 *
 * Browsers block <audio>.play() when there has been no user gesture. Kiosk
 * displays (Fire TV / Google TV / Pi) typically run with autoplay enabled and
 * `mediaPlaybackRequiresUserGesture = false`, so playback starts on first
 * load. In a regular browser the first click anywhere will resume it.
 */
function BackgroundAudio({
  tracks,
  resolveUrl,
}: {
  tracks: ManifestAudioPlaylistItem[];
  resolveUrl: Props['resolveUrl'];
}) {
  const [index, setIndex] = useState(0);
  const [src, setSrc] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Reset to first track when the playlist identity changes.
  const playlistKey = tracks.map((t) => t.id).join('|');
  useEffect(() => {
    setIndex(0);
  }, [playlistKey]);

  const track = tracks.length > 0 ? tracks[index % tracks.length] : null;
  const trackId = track?.id ?? null;
  const trackUrl = track?.url ?? '';
  const trackAssetId = track?.assetId ?? '';
  const trackContentHash = track?.contentHash ?? null;
  const trackVolume = track?.volume ?? 1;

  // Resolve src for the current track; revoke any blob URL on swap.
  useEffect(() => {
    if (!trackId) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    let createdBlobUrl: string | null = null;
    void Promise.resolve(
      resolveUrl(trackUrl, {
        kind: 'asset',
        key: trackAssetId,
        contentHash: trackContentHash,
      }),
    ).then((u) => {
      if (cancelled) return;
      if (u.startsWith('blob:')) createdBlobUrl = u;
      setSrc(u);
    });
    return () => {
      cancelled = true;
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
    };
  }, [trackId, trackUrl, trackAssetId, trackContentHash, resolveUrl]);

  // Apply per-track volume.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !trackId) return;
    el.volume = Math.max(0, Math.min(1, trackVolume));
  }, [trackId, trackVolume]);

  // Best-effort autoplay. Kiosk WebViews honor autoplay; if a regular browser
  // blocks it, NotAllowedError is swallowed and the next interaction will
  // resume playback when the underlying media plays.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !src) return;
    void el.play().catch(() => {
      /* autoplay blocked; will start after a user gesture */
    });
  }, [src]);

  if (!track) return null;

  return (
    <audio
      ref={audioRef}
      src={src ?? undefined}
      autoPlay
      preload="auto"
      onEnded={() => setIndex((i) => (tracks.length === 0 ? 0 : (i + 1) % tracks.length))}
    />
  );
}

function PlaylistItem({
  item,
  resolveUrl,
  onEnd,
}: {
  item: ManifestPlaylistItem;
  resolveUrl: Props['resolveUrl'];
  onEnd: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdBlobUrl: string | null = null;
    void Promise.resolve(
      resolveUrl(item.url ?? '', {
        kind: 'asset',
        key: item.assetId,
        contentHash: item.contentHash ?? null,
      }),
    ).then((u) => {
      if (cancelled) return;
      if (u.startsWith('blob:')) createdBlobUrl = u;
      setSrc(u);
    });
    return () => {
      cancelled = true;
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
    };
  }, [item.url, item.assetId, item.contentHash, resolveUrl]);

  // Image-based timing: advance after durationSeconds.
  useEffect(() => {
    if (item.kind !== 'image') return;
    const ms = Math.max(1, item.durationSeconds) * 1000;
    const t = window.setTimeout(onEnd, ms);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  // Video timing
  const videoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    if (item.kind !== 'video') return;
    if (item.playFullVideo) return; // ended event will fire
    const ms = Math.max(1, item.durationSeconds) * 1000;
    const t = window.setTimeout(onEnd, ms);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const fitClass = useMemo(() => {
    switch (item.fitMode) {
      case 'contain':
        return 'object-contain';
      case 'stretch':
        return 'object-fill';
      case 'cover':
      default:
        return 'object-cover';
    }
  }, [item.fitMode]);

  if (!src) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-black text-xs text-white/40">
        Loading…
      </div>
    );
  }

  if (item.kind === 'image') {
    return (
      <img
        alt=""
        src={src}
        className={`absolute inset-0 h-full w-full ${fitClass} ${
          item.transition === 'fade' ? 'animate-fade-in' : ''
        }`}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      src={src}
      autoPlay
      muted
      playsInline
      onEnded={() => {
        if (item.playFullVideo) onEnd();
      }}
      className={`absolute inset-0 h-full w-full ${fitClass} ${
        item.transition === 'fade' ? 'animate-fade-in' : ''
      }`}
    />
  );
}

function WeatherItem({
  item,
  onEnd,
}: {
  item: ManifestPlaylistItem;
  onEnd: () => void;
}) {
  useEffect(() => {
    const ms = Math.max(1, item.durationSeconds) * 1000;
    const t = window.setTimeout(onEnd, ms);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const zipCode = item.sourceUrl ?? '';
  if (!zipCode) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-black text-xs text-white/40">
        Missing weather ZIP code
      </div>
    );
  }
  return (
    <div
      className={`absolute inset-0 ${
        item.transition === 'fade' ? 'animate-fade-in' : ''
      }`}
    >
      <WeatherWidget zipCode={zipCode} />
    </div>
  );
}

function WebpageItem({
  item,
  onEnd,
}: {
  item: ManifestPlaylistItem;
  onEnd: () => void;
}) {
  useEffect(() => {
    const ms = Math.max(1, item.durationSeconds) * 1000;
    const t = window.setTimeout(onEnd, ms);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const src = prepareVectorSignalWallboardWebpageUrl(item.sourceUrl ?? '');
  const nativeBridge = getNativeTvWebpageBridge();

  useEffect(() => {
    if (!src || !nativeBridge?.showWebpage || !nativeBridge?.hideWebpage) return;
    nativeBridge.showWebpage(src);
    return () => {
      nativeBridge.hideWebpage?.();
    };
  }, [nativeBridge, src]);

  if (!src) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-black text-xs text-white/40">
        Missing webpage URL
      </div>
    );
  }

  if (nativeBridge) {
    return <div className="absolute inset-0 bg-black" />;
  }

  // Webpage media needs `allow-same-origin`: many real sites depend on their
  // normal origin for storage, cookies, and script checks, and otherwise fail
  // as a blank frame. Same-origin URLs are rejected at insert time, so the
  // embedded page keeps its own origin without gaining access to the player's
  // localStorage. pointer-events:none keeps the kiosk from being hijacked by
  // clicks/focus.
  return (
    <iframe
      src={src}
      title=""
      sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
      allow="autoplay; fullscreen; clipboard-read; clipboard-write"
      referrerPolicy="no-referrer-when-downgrade"
      className={`absolute inset-0 h-full w-full border-0 ${
        item.transition === 'fade' ? 'animate-fade-in' : ''
      }`}
      style={{ pointerEvents: 'none' }}
    />
  );
}

function OverlayLayer({
  overlay,
  item,
  resolveUrl,
}: {
  overlay: ManifestOverlay;
  item: ManifestPlaylistItem;
  resolveUrl: Props['resolveUrl'];
}) {
  if (!overlay.enabled) return null;
  if (item.overlayVariant === 'hidden') return null;

  const requestedVariant: OverlayVariantKey =
    item.overlayVariant === 'default'
      ? overlay.defaultVariant
      : (item.overlayVariant as OverlayVariantKey);

  const selected = pickOverlayVariant(overlay, requestedVariant);
  if (!selected) return null;
  const { key: selectedVariantKey, variant } = selected;

  return (
    <div
      style={{
        position: 'absolute',
        left: `${overlay.xPercent}%`,
        top: `${overlay.yPercent}%`,
        width: `${overlay.widthPercent}%`,
        opacity: overlay.opacity,
        transform: anchorTranslate(overlay.anchor),
        pointerEvents: 'none',
      }}
    >
      {variant.kind === 'image' ? (
        <OverlayImage variant={variant} variantKey={selectedVariantKey} resolveUrl={resolveUrl} />
      ) : (
        <OverlayText variant={variant} />
      )}
    </div>
  );
}

function pickOverlayVariant(
  overlay: ManifestOverlay,
  requestedVariant: OverlayVariantKey,
): {
  key: OverlayVariantKey;
  variant: Exclude<ManifestOverlay['variants'][OverlayVariantKey], undefined>;
} | null {
  const keys: OverlayVariantKey[] = [
    requestedVariant,
    overlay.defaultVariant,
    overlay.type === 'text_logo' ? 'text' : 'mono',
    'light',
    'dark',
    'mono',
    'text',
  ];
  const tried = new Set<OverlayVariantKey>();
  for (const key of keys) {
    if (tried.has(key)) continue;
    tried.add(key);
    const variant = overlay.variants[key];
    if (variant) return { key, variant };
  }
  return null;
}

function OverlayImage({
  variant,
  variantKey,
  resolveUrl,
}: {
  variant: ManifestImageVariant;
  variantKey: string;
  resolveUrl: Props['resolveUrl'];
}) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let blob: string | null = null;
    void Promise.resolve(
      resolveUrl(variant.url ?? '', {
        kind: 'overlay',
        key: variantKey,
        contentHash: variant.contentHash ?? null,
      }),
    ).then((u) => {
      if (cancelled) return;
      if (u.startsWith('blob:')) blob = u;
      setSrc(u);
    });
    return () => {
      cancelled = true;
      if (blob) URL.revokeObjectURL(blob);
    };
  }, [variant.url, variant.contentHash, variantKey, resolveUrl]);
  if (!src) return null;
  return <img alt="" src={src} style={{ width: '100%', height: 'auto', display: 'block' }} />;
}

function OverlayText({ variant }: { variant: ManifestTextVariant }) {
  return (
    <div
      style={{
        fontFamily: variant.fontFamily,
        fontWeight: variant.fontWeight,
        color: variant.color,
        letterSpacing: `${variant.letterSpacing}em`,
        // Use viewport-relative size so text scales with screen.
        // fontSize in manifest is interpreted as % of viewport height.
        fontSize: `${variant.fontSize}vh`,
        textShadow: variant.textShadow,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {variant.text}
    </div>
  );
}

function anchorTranslate(anchor: ManifestOverlay['anchor']) {
  switch (anchor) {
    case 'top-left':
      return 'translate(0, 0)';
    case 'top-right':
      return 'translate(-100%, 0)';
    case 'bottom-left':
      return 'translate(0, -100%)';
    case 'bottom-right':
      return 'translate(-100%, -100%)';
    case 'center':
    default:
      return 'translate(-50%, -50%)';
  }
}
